import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, ArrowRight, FileText, Trash2, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-navy/5 text-navy/60 border-navy/10",
  in_progress: "bg-gold/10 text-[#8A6F2A] border-gold/30",
  submitted: "bg-gold text-navy border-gold",
  analyzed: "bg-navy text-ivory border-navy",
  shared: "bg-gold/15 text-[#8A6F2A] border-gold/40",
  transcript_uploaded: "bg-navy/10 text-navy border-navy/20",
  extracted: "bg-gold/20 text-navy border-gold/40",
  reviewed: "bg-navy/15 text-navy border-navy/30",
  documents_generated: "bg-navy text-ivory border-navy",
  ready_for_follow_up: "bg-gold text-navy border-gold",
  converted: "bg-gold/30 text-navy border-gold",
};

const BUSINESS_STAGES = ["", "Startup", "Growing", "Scaling", "Mature", "Turnaround"];
const SERVICE_INTERESTS = [
  "",
  "AI & Operations Assessment",
  "Lead Funnels & AI Automation",
  "30–90 Day Consulting Sprint",
  "Not sure yet",
];

const INTAKE_TYPES = [
  {
    value: "full_audit",
    label: "Full Audit",
    blurb: "Goldline Clarity intake — captures complete business context, transcript extraction, service recommendation, and the 10-document consulting packet.",
  },
  {
    value: "ai_assessment",
    label: "AI Assessment",
    blurb: "Legacy AI Tools Assessment — 6-section intake focused on time-savings, ROI, and the branded PowerPoint deliverable.",
  },
];

const BLANK_FORM = {
  client_name: "",
  business_name: "",
  industry: "",
  intake_type: "full_audit",
  client_email: "",
  client_phone: "",
  website: "",
  location: "",
  team_size: "",
  revenue_range: "",
  business_stage: "",
  main_reason_for_reaching_out: "",
  service_interest: "",
};

export default function DashboardPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const fetchItems = useCallback(async () => {
    try {
      const r = await api.get("/interviews");
      setItems(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/interviews", form);
      setOpen(false);
      setForm({ ...BLANK_FORM });
      setShowMore(false);
      nav(`/interviews/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this intake? This cannot be undone.")) return;
    await api.delete(`/interviews/${id}`);
    fetchItems();
    toast.success("Deleted");
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between mb-12 flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="eyebrow text-gold">Goldline Clarity System</div>
            <h1 className="font-display text-5xl text-navy mt-3" data-testid="dashboard-headline">Client Intakes</h1>
            <div className="w-12 h-[2px] bg-gold mt-4" />
            <p className="text-navy/60 mt-4">
              Capture the client's current systems, identify operational friction, recommend the right
              Goldline service path, and generate the materials needed to move from discovery to execution.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-interview-btn" className="bg-navy hover:bg-navy/90 text-ivory rounded-none h-12 px-6">
                <Plus className="w-4 h-4 mr-2" /> New Intake
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-ivory rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-navy">New Client Intake</DialogTitle>
                <DialogDescription className="text-xs text-navy/50 mt-1">
                  Start with the essentials — add more details below if you have them.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-4" data-testid="new-interview-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gold">Intake Type</Label>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="intake-type-cards">
                    {INTAKE_TYPES.map((t) => {
                      const active = form.intake_type === t.value;
                      return (
                        <button
                          type="button"
                          key={t.value}
                          onClick={() => set("intake_type", t.value)}
                          data-testid={`intake-type-${t.value}`}
                          className={`text-left p-4 border rounded-none transition-colors ${
                            active
                              ? "bg-navy text-ivory border-navy"
                              : "bg-white text-navy border-navy/15 hover:border-gold"
                          }`}
                        >
                          <div className={`eyebrow ${active ? "text-gold" : "text-navy/50"}`}>
                            {active ? "★ Selected" : "Select"}
                          </div>
                          <div className={`font-display text-lg mt-1 ${active ? "text-ivory" : "text-navy"}`}>
                            {t.label}
                          </div>
                          <p className={`text-xs mt-2 leading-relaxed ${active ? "text-ivory/70" : "text-navy/60"}`}>
                            {t.blurb}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client">Client Name *</Label>
                    <Input id="client" data-testid="new-client-name" required value={form.client_name}
                      onChange={(e) => set("client_name", e.target.value)} className="mt-2 rounded-none bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="biz">Business Name</Label>
                    <Input id="biz" data-testid="new-business-name" value={form.business_name}
                      onChange={(e) => set("business_name", e.target.value)} className="mt-2 rounded-none bg-white" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ind">Industry</Label>
                  <Input id="ind" data-testid="new-industry" value={form.industry}
                    onChange={(e) => set("industry", e.target.value)} className="mt-2 rounded-none bg-white" />
                </div>

                <button
                  type="button"
                  onClick={() => setShowMore((v) => !v)}
                  className="text-xs uppercase tracking-wider text-gold inline-flex items-center gap-1 hover:underline"
                  data-testid="toggle-more-details"
                >
                  {showMore ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {showMore ? "Hide" : "Add"} more details
                </button>

                {showMore && (
                  <div className="space-y-4 pt-2 border-t border-navy/10" data-testid="more-details-section">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Client Email</Label>
                        <Input id="email" type="email" data-testid="new-client-email" value={form.client_email}
                          onChange={(e) => set("client_email", e.target.value)} className="mt-2 rounded-none bg-white" />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input id="phone" data-testid="new-client-phone" value={form.client_phone}
                          onChange={(e) => set("client_phone", e.target.value)} className="mt-2 rounded-none bg-white" />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="web">Website</Label>
                        <Input id="web" data-testid="new-website" value={form.website}
                          onChange={(e) => set("website", e.target.value)} className="mt-2 rounded-none bg-white" />
                      </div>
                      <div>
                        <Label htmlFor="loc">Location</Label>
                        <Input id="loc" data-testid="new-location" value={form.location}
                          onChange={(e) => set("location", e.target.value)} className="mt-2 rounded-none bg-white" />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="team">Team size</Label>
                        <Input id="team" data-testid="new-team-size" value={form.team_size}
                          onChange={(e) => set("team_size", e.target.value)} className="mt-2 rounded-none bg-white" />
                      </div>
                      <div>
                        <Label htmlFor="rev">Revenue range</Label>
                        <Input id="rev" placeholder="e.g. $500K–$2M" data-testid="new-revenue-range" value={form.revenue_range}
                          onChange={(e) => set("revenue_range", e.target.value)} className="mt-2 rounded-none bg-white" />
                      </div>
                      <div>
                        <Label htmlFor="stage">Business stage</Label>
                        <select
                          id="stage"
                          data-testid="new-business-stage"
                          value={form.business_stage}
                          onChange={(e) => set("business_stage", e.target.value)}
                          className="mt-2 w-full h-10 px-3 border border-navy/15 rounded-none bg-white text-sm"
                        >
                          {BUSINESS_STAGES.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="reason">Main reason for reaching out</Label>
                      <Input id="reason" data-testid="new-main-reason" value={form.main_reason_for_reaching_out}
                        onChange={(e) => set("main_reason_for_reaching_out", e.target.value)}
                        placeholder="What problem are they trying to solve?"
                        className="mt-2 rounded-none bg-white" />
                    </div>
                    <div>
                      <Label htmlFor="interest">Current service interest</Label>
                      <select
                        id="interest"
                        data-testid="new-service-interest"
                        value={form.service_interest}
                        onChange={(e) => set("service_interest", e.target.value)}
                        className="mt-2 w-full h-10 px-3 border border-navy/15 rounded-none bg-white text-sm"
                      >
                        {SERVICE_INTERESTS.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button type="submit" data-testid="create-interview-submit" className="bg-navy text-ivory rounded-none">
                    Start Intake
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-navy/50">Loading...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-navy/10 p-16 text-center">
            <FileText className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-navy">No intakes yet</h3>
            <p className="text-navy/60 mt-2 mb-6">Create your first client intake to get started.</p>
            <Button onClick={() => setOpen(true)} data-testid="empty-cta-btn" className="bg-navy text-ivory rounded-none">
              <Plus className="w-4 h-4 mr-2" /> New Intake
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="interview-list">
            {items.map((it) => {
              const statusKey = it.status || "draft";
              const statusClass = STATUS_STYLES[statusKey] || STATUS_STYLES.draft;
              const hasTranscript = !!(it.transcript_text || it.transcript_extraction);
              const hasAssessment = !!it.assessment;
              const servicePath = it.recommended_service_path || it.service_recommendation?.primary_service;
              return (
                <div key={it.id}
                  data-testid={`interview-card-${it.id}`}
                  onClick={() => nav(`/interviews/${it.id}`)}
                  className="group bg-white border border-navy/10 p-6 cursor-pointer hover-lift relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`rounded-none uppercase tracking-wider text-[10px] font-semibold ${statusClass}`}>
                        {String(statusKey).replace(/_/g, " ")}
                      </Badge>
                      <Badge
                        variant="outline"
                        data-testid={`card-intake-type-${it.id}`}
                        className={`rounded-none uppercase tracking-wider text-[10px] font-semibold ${
                          (it.intake_type || "full_audit") === "ai_assessment"
                            ? "bg-ivory text-navy/70 border-navy/20"
                            : "bg-navy text-gold border-navy"
                        }`}
                      >
                        {(it.intake_type || "full_audit") === "ai_assessment" ? "AI Assessment" : "Full Audit"}
                      </Badge>
                    </div>
                    <button onClick={(e) => del(it.id, e)} className="opacity-0 group-hover:opacity-100 transition-opacity text-navy/40 hover:text-red-600" data-testid={`delete-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <h3 className="font-display text-2xl text-navy">{it.business_name || it.client_name}</h3>
                  <p className="text-sm text-navy/60 mt-1">{it.client_name} · {it.industry || "—"}</p>

                  {servicePath && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-gold font-semibold" data-testid={`card-service-path-${it.id}`}>
                      <Sparkles className="w-3 h-3" /> {servicePath}
                    </div>
                  )}

                  <div className="w-8 h-[2px] bg-gold mt-5 mb-5" />

                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider text-navy/40 font-semibold">
                    {hasTranscript && <span data-testid={`card-has-transcript-${it.id}`}>· Transcript</span>}
                    {hasAssessment && <span data-testid={`card-has-assessment-${it.id}`}>· Assessment</span>}
                    {it.lead_id && <span data-testid={`card-from-lead-${it.id}`}>· From lead</span>}
                  </div>

                  <div className="flex items-center justify-between text-xs text-navy/50 mt-3">
                    <span>{new Date(it.updated_at).toLocaleDateString()}</span>
                    <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
